import { angularEvents } from "../bridge/angularEvents";
import { reactEvents } from "../bridge/reactEvents";
import { useEffect, useState, useRef } from "react";
import { Formik, Field, FieldArray, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

export interface Option {
  id: string;
  value: string;
}

export interface MetaField {
  id?: number;
  name: string;
  isVisibleOnPrint: boolean;
  positionX: number;
  positionY: number;
  type: string;
  options: Option[];
}

function saveMetaFields(fields: MetaField[]) {
  console.log("Saving meta fields now!");
  reactEvents.saveMetaFields.dispatch(fields);
}

export function MetaFields({
  fields,
  initialIsEditing,
}: {
  fields: MetaField[];
  initialIsEditing: boolean;
}) {
  const [isEditing, setIsEditing] = useState(initialIsEditing);

  // Use a ref to store form values
  const formValuesRef = useRef(fields);

  useEffect(() => {
    const unsub = angularEvents.isEditing.subscribe((e: boolean) => {
      setIsEditing(e);
    });

    const unsubSaveArticle = angularEvents.saveArticle.subscribe(() => {
      saveMetaFields(formValuesRef.current);
    });

    return () => {
      unsub();
      unsubSaveArticle();
    };
  }, []);

  useEffect(() => {
    console.log(`isEditing changed: ${isEditing}`);
  }, [isEditing]);

  const initialValues = {
    metaFields: fields.map((field) => ({
      ...field,
      options: field.options || [],
      newOption: "",
    })),
  };

  const typeOptions = ["short text", "long text", "number", "date", "list"];

  return (
    <div>
      <h2>Meta fields</h2>
      <p>
        Apply to all articles, only values can be added on the article, see Meta
        field values above
      </p>
      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object({
            metaFields: Yup.array().of(
              Yup.object().shape({
                name: Yup.string().required("Name is required"),
                type: Yup.string().required("Type is required")
              })
            ),
          })}
          onSubmit={async (values) => {
            await new Promise((r) => setTimeout(r, 500));
            console.log(JSON.stringify(values, null, 2));
            formValuesRef.current = values.metaFields; // Update the ref with the latest form data
          }}
        >
          {({ values, setFieldValue }) => {
            // Update formValuesRef on every render to keep it up to date with form values
            formValuesRef.current = values.metaFields;

            return (
              <Form>
                <FieldArray name="metaFields">
                  {({ remove, push }) => (
                    <div>
                      {values.metaFields.length > 0 &&
                        values.metaFields.map((metaField, index) => (
                          <div className="row" key={index}>
                            <div className="col" style={{ padding: "10px" }}>
                              <label htmlFor={`metaFields.${index}.name`}>
                                Name
                              </label>
                              <Field
                                name={`metaFields.${index}.name`}
                                type="text"
                                disabled={!isEditing}
                              />
                              <ErrorMessage
                                name={`metaFields.${index}.name`}
                                component="div"
                                className="field-error"
                              />
                            </div>
                            <div className="col" style={{ padding: "10px" }}>
                              <label htmlFor={`metaFields.${index}.type`}>
                                Type
                              </label>
                              <Field
                                name={`metaFields.${index}.type`}
                                as="select"
                                disabled={!isEditing}
                              >
                                <option value="">Select a type</option>
                                {typeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage
                                name={`metaFields.${index}.type`}
                                component="div"
                                className="field-error"
                              />
                            </div>
                            <div className="col" style={{ padding: "10px" }}>
                              <label>
                                <Field
                                  name={`metaFields.${index}.isVisibleOnPrint`}
                                  type="checkbox"
                                  disabled={!isEditing}
                                />
                                Visible on Print
                              </label>
                            </div>
                            {/* Only show these controls if the selected type is "list" */}
                            {metaField.type === "list" && (
                              <>
                                {isEditing ? (
                                  <div
                                    className="col"
                                    style={{ padding: "10px" }}
                                  >
                                    <label
                                      htmlFor={`metaFields.${index}.newOption`}
                                    >
                                      Add Option
                                    </label>
                                    <input
                                      type="text"
                                      id={`metaFields.${index}.newOption`}
                                      value={metaField.newOption || ""}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `metaFields.${index}.newOption`,
                                          e.target.value
                                        )
                                      }
                                      disabled={!isEditing}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (metaField.newOption) {
                                          const newOption = {
                                            value: metaField.newOption,
                                          };
                                          const updatedOptions = [
                                            ...metaField.options,
                                            newOption,
                                          ];
                                          setFieldValue(
                                            `metaFields.${index}.options`,
                                            updatedOptions
                                          );
                                          setFieldValue(
                                            `metaFields.${index}.newOption`,
                                            ""
                                          ); 
                                        }
                                      }}
                                      disabled={!isEditing}
                                    >
                                      Add
                                    </button>
                                    <div
                                      className="col"
                                      style={{ padding: "10px" }}
                                    >
                                      <label>Options:</label>
                                      <ul>
                                        {metaField.options.map((option, i) => (
                                          <li key={option.value}>
                                            {option.value}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedOptions =
                                                  metaField.options.filter(
                                                    (_, optIndex) =>
                                                      optIndex !== i
                                                  );
                                                setFieldValue(
                                                  `metaFields.${index}.options`,
                                                  updatedOptions
                                                );
                                              }}
                                              disabled={!isEditing}
                                            >
                                              Remove
                                            </button>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="col"
                                    style={{ padding: "10px" }}
                                  >
                                    <label>Options:</label>
                                    <select defaultValue="">
                                      <option value="">View options</option>
                                      {metaField.options.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.value}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </>
                            )}
                            {isEditing && (
                              <div className="col" style={{ padding: "10px" }}>
                                <button
                                  type="button"
                                  className="secondary"
                                  onClick={() => remove(index)}
                                  disabled={!isEditing}
                                >
                                  X
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      {isEditing && (
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            push({
                              name: "",
                              type: "",
                              isVisibleOnPrint: false,
                              positionX: 1,
                              positionY: 1,
                              options: [],
                            })
                          }
                          disabled={!isEditing}
                        >
                          Add meta field
                        </button>
                      )}
                    </div>
                  )}
                </FieldArray>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
